import React from 'react'
import { MinGuestsCounter } from 'components/molecules/MinGuestsCounter'
import { MultiDatePicker } from 'components/molecules/MultiDatePicker'
import moment from 'moment'
import { IFeature } from 'static/interfaces'
import { ISelectedGuests } from 'utils/reservation'
import { SearchAreaCard } from './SearchAreaCard'
import styled from 'styled-components'
import {
  COLORS,
  DEFAULT_SEARCH_FILTER_GUESTS,
  BREAKPOINT_TABLET_SMALL,
  HAS_FIXED_FOOTER_CLASS,
} from 'static/constants'
import { useSearchFilters } from 'utils/useSearchFilters'
import { useBodyClassForMediaQuery } from 'utils/hooks'
import SvgIcon from 'components/atoms/SvgIcon'
import { IconName } from '../../types/icon-names'

interface IMobileSearchPanelProps {
  date: moment.Moment | null
  guests: ISelectedGuests
  areaFeature: IFeature | null
  setAreaFeature: (areaFeature: IFeature | null) => void
  setDate: (date: moment.Moment | null) => void
  setGuests: (guests: ISelectedGuests) => void
  areaFeatures: IFeature[]
  setSearchBoxIsOpen: (isOpen: boolean) => void
}

interface ISearchPanelSection {
  icon: IconName
  label: string
  placeholder: string
  value: string | number | null | ISelectedGuests
  reset: () => void
}

// TODO 親コンポーネントつくってフィルタの状態を親が持って子に渡すように作り変える

const SearchPanelSection = ({ icon, label, placeholder, value, reset }: ISearchPanelSection) => {
  return (
    <SearchPanelSectionWrapper>
      <div className="label">
        <SvgIcon name={icon} className="icon" />
        <p>{label}</p>
      </div>
      <div className="toggle">
        {value ? (
          <span className="filterValue">{value}</span>
        ) : (
          <span className="placeholder">{placeholder}</span>
        )}
        {value ? (
          <SvgIcon
            name="close"
            className="icon"
            onClick={e => {
              e.stopPropagation()
              reset()
            }}
          />
        ) : (
          <SvgIcon name="keyboard_arrow_down" className="icon" />
        )}
      </div>
    </SearchPanelSectionWrapper>
  )
}

const SearchPanel = ({
  titleSection,
  content,
  isOpen,
  setIsOpen,
}: {
  titleSection: React.ReactNode
  content: React.ReactNode
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  return (
    <SearchSection>
      <div onClick={() => setIsOpen(old => !old)}>{titleSection}</div>
      <TogglePanel className={isOpen ? 'open' : ''}>{content}</TogglePanel>
    </SearchSection>
  )
}

export const MobileHeaderSearchPanel: React.VFC<IMobileSearchPanelProps> = ({
  date,
  guests,
  areaFeature,
  setAreaFeature,
  setDate,
  setGuests,
  areaFeatures,
  setSearchBoxIsOpen,
}) => {
  const [sending, setSending] = React.useState(false)
  const [isOpenArea, setIsOpenArea] = React.useState(false)
  const [isOpenDate, setIsOpenDate] = React.useState(false)
  const [isOpenGuests, setIsOpenGuests] = React.useState(false)

  const dateLabel = React.useMemo(() => {
    if (date) {
      return `${date?.format('MMM DD')}`
    }
    return null
  }, [date])

  const { fireSearchEvent, saveSearchFilters, searchHref } = useSearchFilters({
    date,
    guests,
    areaFeature,
  })
  const didMountRef = React.useRef(false)

  useBodyClassForMediaQuery(HAS_FIXED_FOOTER_CLASS, `(max-width: ${BREAKPOINT_TABLET_SMALL - 1}px)`)

  React.useEffect(() => {
    // 初回レンダリング時は自動発火を防ぐ
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }

    saveSearchFilters()
  }, [saveSearchFilters])

  const search = React.useCallback(async () => {
    try {
      setSending(true)
      await fireSearchEvent(() => {
        location.href = searchHref
      })
    } finally {
      setSending(false)
    }
  }, [searchHref, fireSearchEvent])

  const reset = () => {
    setAreaFeature(null)
    setDate(null)
    setGuests(DEFAULT_SEARCH_FILTER_GUESTS)
  }
  const totalGuests = guests.adults + guests.children + guests.infants

  return (
    <MobileSearchPanel>
      <div className="MobileSearch_Header">
        <div className="Close" onClick={() => setSearchBoxIsOpen(false)}>
          <SvgIcon name="close" className="icon" ariaHidden={true} role="button" />
        </div>
        <div className="MobileSearch_Title">Search Tours</div>
      </div>
      <div className="MobileSearch_Body">
        <SearchPanel
          isOpen={isOpenArea}
          setIsOpen={setIsOpenArea}
          titleSection={
            <SearchPanelSection
              icon="place"
              label="WHERE"
              placeholder="FIND PLACE"
              value={areaFeature?.title}
              reset={() => setAreaFeature(null)}
            />
          }
          content={areaFeatures.map(feature => (
            <SearchAreaCard
              key={feature.id}
              isMobile={true}
              currentAreaFeature={areaFeature}
              areaFeature={feature}
              setAreaFeature={selectedFeature => {
                setAreaFeature(selectedFeature)
                setIsOpenArea(false)
              }}
            />
          ))}
        />
        <SearchPanel
          isOpen={isOpenDate}
          setIsOpen={setIsOpenDate}
          titleSection={
            <SearchPanelSection
              icon="event_note"
              label="DATES"
              placeholder="ADD DATE"
              value={dateLabel}
              reset={() => setDate(null)}
            />
          }
          content={
            <MultiDatePicker
              forSingleDate={true}
              noShadow={true}
              autoApply={true}
              selectedModalDate={date ?? moment()}
              dates={date ? [date] : []}
              setDates={(dates: moment.Moment[]) => {
                if (dates.length > 0) {
                  setDate(dates[0])
                } else {
                  setDate(null)
                }
              }}
              setIsOpenDatePicker={setIsOpenDate}
            />
          }
        />
        <SearchPanel
          isOpen={isOpenGuests}
          setIsOpen={setIsOpenGuests}
          titleSection={
            <SearchPanelSection
              icon="people"
              label="GUESTS"
              placeholder="ADD GUESTS"
              value={totalGuests > 0 ? `${totalGuests} guests` : null}
              reset={() => setGuests(DEFAULT_SEARCH_FILTER_GUESTS)}
            />
          }
          content={
            <MinGuestsCounter
              relative={true}
              isPrivate={false}
              autoApply={true}
              guests={guests}
              setGuests={setGuests}
              setIsOpenGuestCount={setIsOpenGuests}
            />
          }
        />
      </div>
      <div className="MobileSearch_Footer">
        <div className="clear" onClick={reset}>
          Clear all
        </div>
        <button className="searchButton" onClick={search} disabled={sending}>
          Search
        </button>
      </div>
    </MobileSearchPanel>
  )
}

const MobileSearchPanel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  min-height: 100dvh;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  background-color: ${COLORS.background2};
  z-index: 10;
  display: flex;
  flex-direction: column;

  .MobileSearch_Header {
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    background: ${COLORS.background2};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 54px;
    width: 100%;
    flex: none;
    z-index: 10;

    .MobileSearch_Title {
      font-weight: 700;
      font-size: 20px;
    }
    .Close {
      position: absolute;
      top: 10px;
      left: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      cursor: pointer;
      border-radius: 16px;
      border: 1px solid ${COLORS.divider2};
      .icon {
        width: 24px;
        height: 24px;
      }
    }
  }
  .MobileSearch_Body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin: 23px 20px 29px;
    padding-bottom: 64px;
    flex: 1;
  }

  .MobileSearch_Footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    background: ${COLORS.white};
    box-shadow: ${COLORS.cardShadow};

    flex: none;
    z-index: 10;

    .clear {
      font-size: 15px;
      font-weight: 500;
      text-decoration: underline;
      cursor: pointer;
    }
    .searchButton {
      width: 150px;
      height: 44px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 13px;
      font-weight: 700;
      color: ${COLORS.white};
      background: ${COLORS.info3};
      border-radius: 12px;
      border: none;
      cursor: pointer;
      &:hover {
        background-color: ${COLORS.info4};
      }

      &:disabled {
        background-color: ${COLORS.disabled};
      }
    }
  }
`
const SearchSection = styled.div`
  display: flex;
  flex-direction: column;

  padding: 24px 16px;
  background: ${COLORS.white};
  box-shadow: ${COLORS.cardShadow};
  border-radius: 12px;
`
const TogglePanel = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  height: 0;
  overflow: hidden;

  &.open {
    margin-top: 32px;
    height: auto;
  }
`

const SearchPanelSectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  .label {
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${COLORS.subText};
    .icon {
      color: ${COLORS.primary};
      width: 24px;
      height: 24px;
    }
  }
  .toggle {
    color: ${COLORS.divider};
    display: flex;
    align-items: center;
    gap: 10px;
    .filterValue,
    .icon {
      color: ${COLORS.black};
      width: 24px;
      height: 24px;
    }
    .filterValue {
      width: auto;
      font-weight: 500;
    }
  }
`
